/**
 * HomePage Data Service
 * Handles all API calls and data fetching for the homepage
 */

import logger from "../../logger";

/**
 * Fetches approve lines data from API
 * @param {Function} execute - MSAL fetch execution function
 * @returns {Promise<number>} Count of orders to approve
 */
export const fetchApproveLinesData = async (execute) => {
  try {
    const response = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approveLines/getOrdersToHome`
    );

    return response.count || 0;
  } catch (error) {
    logger.error("Failed to fetch approve lines data", {
      error: error.message,
      endpoint: "/api/approveLines/getOrdersToHome",
    });
    throw error;
  }
};

/**
 * Fetches obligo exceptions data from API
 * @param {Function} execute - MSAL fetch execution function
 * @returns {Promise<Object>} Object containing count and details array
 */
export const fetchObligoExceptionsData = async (execute) => {
  try {
    const response = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/exceedObligoOrders`
    );

    return {
      count: response.exceedOrders?.length || 0,
      details: response.exceedOrders || [],
    };
  } catch (error) {
    logger.error("Failed to fetch obligo exceptions data", {
      error: error.message,
      endpoint: "/api/financialData/exceedObligoOrders",
    });
    throw error;
  }
};

/**
 * Fetches orders log history data from API
 * @param {Function} execute - MSAL fetch execution function
 * @returns {Promise<Object>} Orders log data
 */
export const fetchOrdersLogData = async (execute) => {
  try {
    const response = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/ordersLog`
    );

    return response;
  } catch (error) {
    logger.error("Failed to fetch orders log data", {
      error: error.message,
      endpoint: "/api/financialData/ordersLog",
    });
    throw error;
  }
};

/**
 * Fetches all homepage data based on user permissions
 * Coordinates multiple API calls and returns consolidated data
 * @param {Function} execute - MSAL fetch execution function
 * @param {Object} accessRights - User's access rights object
 * @returns {Promise<Object>} Consolidated homepage data
 */
export const fetchAllHomePageData = async (execute, accessRights) => {
  const results = {
    approveLines: { count: 0, error: null },
    obligoExceptions: { count: 0, details: [], error: null },
    ordersLog: { data: null, error: null },
  };

  try {
    // Fetch approve lines if user has access
    if (accessRights?.AproveLinesPage) {
      try {
        results.approveLines.count = await fetchApproveLinesData(execute);
      } catch (error) {
        results.approveLines.error = error;
      }
    }

    // Fetch obligo data if user has access
    if (accessRights?.ObligoExceptionPage || accessRights?.ViewObligoData) {
      try {
        const obligoData = await fetchObligoExceptionsData(execute);
        results.obligoExceptions = { ...obligoData, error: null };
      } catch (error) {
        results.obligoExceptions.error = error;
      }

      try {
        results.ordersLog.data = await fetchOrdersLogData(execute);
      } catch (error) {
        results.ordersLog.error = error;
      }
    }
  } catch (error) {
    logger.error("Unexpected error in fetchAllHomePageData", {
      error: error.message,
    });
  }

  return results;
};
